/**
 * Data Table Component
 * Reusable table with pagination, loading, and empty states
 * Optimized with memoization and optional virtualization for better performance
 */

import React, { useCallback, useMemo, useRef } from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Checkbox,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { EmptyState, EmptyStateProps } from "./EmptyState";
import { TableSkeleton } from "./LoadingState";
import { ErrorState, getErrorMessage } from "./ErrorState";
import { RowLink } from "./RowLink";

// Row height for virtualization calculations
const ROW_HEIGHT = 53;
// Threshold for enabling virtualization automatically
const VIRTUALIZATION_THRESHOLD = 100;

// Shared header-cell treatment: quiet uppercase, spaced, secondary — the
// premium data-table header look. Scoped to DataTable so it doesn't force
// uppercase on bespoke tables elsewhere (e.g. the dashboard growth table).
// Hoisted to module scope so it isn't reallocated on every render.
const HEAD_CELL_SX = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  color: "text.secondary",
  whiteSpace: "nowrap" as const,
};

export interface Column<T> {
  /** Unique key for the column */
  key: string;
  /** Column header label */
  header: string;
  /** Cell renderer */
  render: (item: T, index: number) => React.ReactNode;
  /** Column width */
  width?: number | string;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Whether column is sortable */
  sortable?: boolean;
  /** Whether to hide on mobile */
  hideOnMobile?: boolean;
}

export interface RowAction<T> {
  /** Action label */
  label: string;
  /** Action icon */
  icon?: React.ReactNode;
  /** Action callback */
  onClick: (item: T) => void;
  /** Whether action is disabled */
  disabled?: boolean | ((item: T) => boolean);
  /** Whether action is destructive */
  destructive?: boolean;
  /** Whether to show divider before this action */
  divider?: boolean;
  /** Whether to hide this action for specific item */
  hidden?: (item: T) => boolean;
}

export interface DataTableProps<T> {
  /** Data array */
  data: T[];
  /** Column definitions */
  columns: Column<T>[];
  /** Unique key extractor */
  getRowKey: (item: T) => string | number;
  /** Row actions (shown in menu) */
  rowActions?: RowAction<T>[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error object */
  error?: unknown;
  /** Retry callback for errors */
  onRetry?: () => void;
  /** Total count for pagination */
  total?: number;
  /** Current page (0-indexed) */
  page?: number;
  /** Rows per page */
  rowsPerPage?: number;
  /** Page change handler */
  onPageChange?: (page: number) => void;
  /** Rows per page change handler */
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /** Rows per page options */
  rowsPerPageOptions?: number[];
  /** Empty state props */
  emptyState?: Partial<EmptyStateProps>;
  /** Search empty state (when filtering returns no results) */
  searchEmptyState?: Partial<EmptyStateProps>;
  /** Whether current view is filtered */
  isFiltered?: boolean;
  /** Sort column */
  sortBy?: string;
  /** Sort direction */
  sortDirection?: "asc" | "desc";
  /** Sort change handler */
  onSort?: (column: string, direction: "asc" | "desc") => void;
  /** Whether to enable row selection */
  selectable?: boolean;
  /** Selected row keys */
  selectedKeys?: (string | number)[];
  /** Selection change handler */
  onSelectionChange?: (keys: (string | number)[]) => void;
  /** Row click handler */
  onRowClick?: (item: T) => void;
  /**
   * Target URL for a row. When provided, the row becomes link-like: the
   * primary (first) cell renders a real anchor so right-click "Open in new
   * tab" works, and Ctrl/Cmd-click or middle-click anywhere on the row
   * opens the target in a new tab. Plain left-click is unchanged (uses
   * onRowClick). Should point to the same destination as onRowClick.
   */
  getRowHref?: (item: T) => string;
  /** Optional per-row sx (e.g. highlight refunds / overdue rows). Merged after
   *  the base row styles. */
  getRowSx?: (item: T) => SxProps<Theme>;
  /** Whether to wrap in a Card */
  showCard?: boolean;
  /** Skeleton rows count for loading state */
  skeletonRows?: number;
  /** Custom className */
  className?: string;
  /** ARIA label for the table element */
  ariaLabel?: string;
  /** Enable virtualization for large datasets (auto-enabled when data > 100 rows) */
  enableVirtualization?: boolean;
  /** Max height for virtualized table container */
  virtualizedMaxHeight?: number;
  /**
   * Minimum table width (px). Below this the container scrolls horizontally
   * instead of squishing columns — so wide tables stay readable on phones.
   * Set lower for narrow tables to avoid unnecessary scroll.
   */
  minWidth?: number;
}

export function DataTable<T>({
  data,
  columns,
  getRowKey,
  rowActions,
  isLoading = false,
  error,
  onRetry,
  total,
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 20, 50, 100],
  emptyState,
  searchEmptyState,
  isFiltered = false,
  sortBy,
  sortDirection = "asc",
  onSort,
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  onRowClick,
  getRowHref,
  getRowSx,
  showCard = true,
  skeletonRows = 5,
  enableVirtualization,
  virtualizedMaxHeight = 600,
  minWidth = 650,
  ariaLabel = "Data table",
}: DataTableProps<T>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = React.useState<T | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Auto-enable virtualization for large datasets
  const shouldVirtualize = enableVirtualization ?? data.length > VIRTUALIZATION_THRESHOLD;

  // Virtualizer for large datasets
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, item: T) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedItem(null);
  }, []);

  const handleActionClick = useCallback((action: RowAction<T>) => {
    if (selectedItem) {
      action.onClick(selectedItem);
    }
    setAnchorEl(null);
    setSelectedItem(null);
  }, [selectedItem]);

  const handleSelectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelectionChange) {
      if (event.target.checked) {
        onSelectionChange(data.map(getRowKey));
      } else {
        onSelectionChange([]);
      }
    }
  }, [onSelectionChange, data, getRowKey]);

  const handleSelectRow = useCallback((key: string | number) => {
    if (onSelectionChange) {
      const isSelected = selectedKeys.includes(key);
      if (isSelected) {
        onSelectionChange(selectedKeys.filter((k) => k !== key));
      } else {
        onSelectionChange([...selectedKeys, key]);
      }
    }
  }, [onSelectionChange, selectedKeys]);

  const handleSortClick = useCallback((column: string) => {
    if (onSort) {
      const newDirection =
        sortBy === column && sortDirection === "asc" ? "desc" : "asc";
      onSort(column, newDirection);
    }
  }, [onSort, sortBy, sortDirection]);

  // Render a single row - must be defined before early returns for hooks rules
  const renderRow = useCallback(
    (item: T, index: number, style?: React.CSSProperties) => {
      const key = getRowKey(item);
      const isSelected = selectedKeys.includes(key);
      const href = getRowHref?.(item);

      return (
        <TableRow
          key={key}
          hover
          selected={isSelected}
          onClick={
            href || onRowClick || selectable
              ? (e: React.MouseEvent) => {
                  // Ctrl/Cmd-click -> new tab (mirrors anchor behavior).
                  if (href && (e.metaKey || e.ctrlKey)) {
                    window.open(href, "_blank", "noopener,noreferrer");
                    return;
                  }
                  // Plain click is unchanged.
                  if (onRowClick) onRowClick(item);
                  else if (selectable) handleSelectRow(key);
                }
              : undefined
          }
          onAuxClick={
            href
              ? (e: React.MouseEvent) => {
                  // Middle-click -> new tab.
                  if (e.button === 1) {
                    e.preventDefault();
                    window.open(href, "_blank", "noopener,noreferrer");
                  }
                }
              : undefined
          }
          sx={{
            cursor: href || onRowClick || selectable ? "pointer" : "default",
            ...(style && { height: style.height }),
            ...(getRowSx?.(item) as object),
          }}
          style={style}
        >
          {selectable && (
            <TableCell padding="checkbox">
              <Checkbox
                checked={isSelected}
                onClick={(e) => e.stopPropagation()}
                onChange={() => handleSelectRow(key)}
                slotProps={{
                  input: { "aria-label": "Select row" }
                }}
              />
            </TableCell>
          )}
          {columns.map((column, colIndex) => (
            <TableCell
              key={column.key}
              align={column.align}
              sx={{
                display: column.hideOnMobile
                  ? { xs: "none", md: "table-cell" }
                  : undefined,
              }}
            >
              {/* Primary cell becomes a real link so right-click "Open in
                  new tab" works and the row is keyboard reachable. */}
              {href && colIndex === 0 ? (
                <RowLink to={href}>{column.render(item, index)}</RowLink>
              ) : (
                column.render(item, index)
              )}
            </TableCell>
          ))}
          {rowActions && rowActions.length > 0 && (
            <TableCell align="right">
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, item)}
                aria-label="Row actions"
              >
                <MoreVertIcon />
              </IconButton>
            </TableCell>
          )}
        </TableRow>
      );
    },
    [
      getRowKey,
      selectedKeys,
      onRowClick,
      getRowHref,
      getRowSx,
      selectable,
      handleSelectRow,
      columns,
      rowActions,
      handleMenuOpen,
    ]
  );

  // Memoize empty state to avoid unnecessary recalculations
  const defaultEmptyState = useMemo((): EmptyStateProps => (
    isFiltered
      ? {
          title: "No results found",
          description: "Try adjusting your search or filter criteria",
          isSearchResult: true,
          ...searchEmptyState,
        }
      : {
          title: "No data yet",
          description: "Get started by adding your first item",
          ...emptyState,
        }
  ), [isFiltered, searchEmptyState, emptyState]);

  // Show loading state
  if (isLoading) {
    return (
      <TableSkeleton
        rows={skeletonRows}
        columns={columns.length + (rowActions ? 1 : 0) + (selectable ? 1 : 0)}
        showCard={showCard}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <ErrorState
        title="Error Loading Data"
        message={getErrorMessage(error)}
        onRetry={onRetry}
        variant="alert"
      />
    );
  }

  // Show empty state
  if (data.length === 0) {
    const Wrapper = showCard ? Card : React.Fragment;
    return (
      <Wrapper>
        <EmptyState {...defaultEmptyState} />
      </Wrapper>
    );
  }

  // Render table header
  const tableHeader = (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={
                selectedKeys.length > 0 && selectedKeys.length < data.length
              }
              checked={data.length > 0 && selectedKeys.length === data.length}
              onChange={handleSelectAll}
              slotProps={{
                input: { "aria-label": "Select all" }
              }}
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.key}
            align={column.align}
            width={column.width}
            // Announce current sort to screen readers (WCAG 1.3.1/4.1.2).
            aria-sort={
              column.sortable && onSort && sortBy === column.key
                ? sortDirection === "asc"
                  ? "ascending"
                  : "descending"
                : undefined
            }
            sx={{
              ...HEAD_CELL_SX,
              display: column.hideOnMobile
                ? { xs: "none", md: "table-cell" }
                : undefined,
            }}
          >
            {column.sortable && onSort ? (
              <TableSortLabel
                active={sortBy === column.key}
                direction={sortBy === column.key ? sortDirection : "asc"}
                onClick={() => handleSortClick(column.key)}
              >
                {column.header}
              </TableSortLabel>
            ) : (
              column.header
            )}
          </TableCell>
        ))}
        {rowActions && rowActions.length > 0 && (
          <TableCell align="right" width={60} sx={HEAD_CELL_SX}>
            Actions
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );

  // Virtualized table content
  const virtualizedTableContent = (
    <>
      <TableContainer
        ref={tableContainerRef}
        sx={{ maxHeight: virtualizedMaxHeight, overflow: "auto" }}
      >
        <Table aria-label={ariaLabel} sx={{ tableLayout: "fixed", minWidth }}>
          {tableHeader}
          <TableBody>
            {/* Spacer row for virtualized content above visible area */}
            <tr style={{ height: virtualizer.getVirtualItems()[0]?.start ?? 0 }} />
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = data[virtualRow.index];
              return renderRow(item, virtualRow.index, {
                height: `${virtualRow.size}px`,
              });
            })}
            {/* Spacer row for virtualized content below visible area */}
            <tr
              style={{
                height:
                  virtualizer.getTotalSize() -
                  (virtualizer.getVirtualItems()[
                    virtualizer.getVirtualItems().length - 1
                  ]?.end ?? 0),
              }}
            />
          </TableBody>
        </Table>
      </TableContainer>

      {total !== undefined && onPageChange && onRowsPerPageChange && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            onRowsPerPageChange(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </>
  );

  // Standard (non-virtualized) table content
  const tableContent = (
    <>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table aria-label={ariaLabel} sx={{ minWidth }}>
          {tableHeader}
          <TableBody>
            {data.map((item, index) => renderRow(item, index))}
          </TableBody>
        </Table>
      </TableContainer>

      {total !== undefined && onPageChange && onRowsPerPageChange && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            onRowsPerPageChange(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </>
  );

  const content = shouldVirtualize ? virtualizedTableContent : tableContent;

  return (
    <>
      {showCard ? <Card>{content}</Card> : content}

      {/* Actions Menu */}
      {rowActions && rowActions.length > 0 && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          aria-label="Row actions menu"
        >
          {rowActions.map((action, index) => {
            const isDisabled =
              typeof action.disabled === "function"
                ? selectedItem
                  ? action.disabled(selectedItem)
                  : false
                : action.disabled;

            const isHidden =
              action.hidden && selectedItem ? action.hidden(selectedItem) : false;

            if (isHidden) return null;

            return (
              <React.Fragment key={index}>
                {action.divider && index > 0 && <Divider />}
                <MenuItem
                  onClick={() => handleActionClick(action)}
                  disabled={isDisabled}
                  sx={action.destructive ? { color: "error.main" } : undefined}
                >
                  {action.icon && (
                    <ListItemIcon
                      sx={action.destructive ? { color: "error.main" } : undefined}
                    >
                      {action.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText>{action.label}</ListItemText>
                </MenuItem>
              </React.Fragment>
            );
          })}
        </Menu>
      )}
    </>
  );
}

export default DataTable;
